import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  Check,
  ChevronDown,
  CircleHelp,
  Clipboard,
  Copy,
  Download,
  ExternalLink,
  Gauge,
  Laptop,
  Monitor,
  QrCode,
  Radio,
  Send,
  Settings2,
  ShieldCheck,
  Users,
  Wifi,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  FALLBACK_MANIFEST,
  detectRecommendedDownload,
  formatBytes,
  isDownloadManifest
} from "./downloads";
import { startWebsiteAnalytics, trackWebsiteEvent } from "./analytics";

const HERO_VIDEO_URL = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260423_084718_72a17915-4964-4059-afcd-22d59399b72e.mp4";

interface Workflow {
  title: string;
  description: string;
  icon: LucideIcon;
}

const workflows: Workflow[] = [
  {
    title: "账号集中就绪",
    description: "添加运营账号，启动独立浏览器窗口，在一张表里确认登录、网络和页面状态。",
    icon: Users
  },
  {
    title: "直播间同步切换",
    description: "从关注主播中选择正在直播的场次，已选账号立即进入同一直播间。",
    icon: Radio
  },
  {
    title: "评论发送可核对",
    description: "设定内容、连发数量和间隔，发送后按账号查看成功数与执行状态。",
    icon: Send
  }
];

const faqs = [
  ["SFast 目前支持哪些直播平台？", "当前版本支持淘宝直播、小红书和抖音，帮助运营团队集中管理多平台账号、直播间与发送流程。"],
  ["macOS 应该下载哪个版本？", "下载 macOS 通用版即可，同时支持 Apple 芯片与 Intel 芯片，要求 macOS 12 或更高版本。"],
  ["Windows x64 和 ARM64 怎么选？", "绝大多数 Intel、AMD 电脑选择 x64；使用 ARM 处理器的 Windows 设备选择 ARM64。页面会尽可能自动推荐，你也可以手动切换。"],
  ["账号浏览器有什么要求？", "SFast 优先使用本机 Chrome；未安装 Chrome 时，Windows 可使用 Edge。Windows 控制台依赖系统 WebView2。"],
  ["第一次使用为什么需要授权？", "SFast 是授权软件。安装后注册或登录账号，再输入管理员发放的授权码；获取授权可添加微信 ssjnpm。"],
  ["后续版本如何更新？", "1.0.6 及以上版本支持在软件内检查在线更新。官网始终提供当前版本的完整安装包。"]
];

interface BrandProps {
  compact?: boolean;
  inverse?: boolean;
}

function Brand({ compact = false, inverse = false }: BrandProps) {
  return (
    <a className={`brand${inverse ? " brand-inverse" : ""}`} href="#top" aria-label="SFast 首页">
      <img src="/favicon.svg" alt="" />
      <span>
        <strong>SFast</strong>
        {!compact && <small>LIVE OPS CONSOLE</small>}
      </span>
    </a>
  );
}

interface AnimatedHeadingProps {
  id?: string;
  text: string;
  className?: string;
  style?: CSSProperties;
  delay?: number;
  charDelay?: number;
}

function AnimatedHeading({ id, text, className = "", style, delay = 0, charDelay = 30 }: AnimatedHeadingProps) {
  const [visible, setVisible] = useState(false);
  const lines = text.split("\n");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);

  return (
    <h1 id={id} className={className} style={style} aria-label={lines.join(" ")}>
      {lines.map((line, lineIndex) => (
        <span className="block" key={`${line}-${lineIndex}`} aria-hidden="true">
          {[...line].map((character, characterIndex) => (
            <span
              className={`inline-block transition-all duration-500 ${visible ? "translate-x-0 opacity-100" : "-translate-x-[18px] opacity-0"}`}
              style={{ transitionDelay: visible ? `${lineIndex * line.length * charDelay + characterIndex * charDelay}ms` : "0ms" }}
              key={`${character}-${characterIndex}`}
            >
              {character === " " ? "\u00A0" : character}
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
}

interface FadeInProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
}

function FadeIn({ children, className = "", delay = 0, duration = 800 }: FadeInProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-opacity ${visible ? "opacity-100" : "opacity-0"} ${className}`}
      style={{ transitionDuration: `${duration}ms` }}
    >
      {children}
    </div>
  );
}

function PlatformIcon({ platform }: { platform: string }) {
  return platform === "macos" ? <Laptop aria-hidden="true" /> : <Monitor aria-hidden="true" />;
}

interface ContactDialogProps {
  onClose: () => void;
  onCopy?: () => void;
}

function ContactDialog({ onClose, onCopy }: ContactDialogProps) {
  const [copied, setCopied] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [onClose]);

  const copyWechat = async () => {
    onCopy?.();
    await navigator.clipboard?.writeText("ssjnpm");
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="contact-dialog" role="dialog" aria-modal="true" aria-labelledby="contact-title">
        <button className="icon-button close-button" type="button" onClick={onClose} aria-label="关闭" ref={closeButton}><X /></button>
        <div className="contact-copy">
          <span className="section-kicker"><QrCode size={15} /> 获取授权</span>
          <h2 id="contact-title">添加微信，了解授权</h2>
          <p>扫码添加，或复制微信号后在微信中搜索。</p>
          <button className="wechat-copy" type="button" onClick={copyWechat}>
            <span><small>微信号</small><strong>ssjnpm</strong></span>
            {copied ? <><Check size={17} /> 已复制</> : <><Copy size={17} /> 复制</>}
          </button>
        </div>
        <div className="qr-frame"><img src="/images/wechat-qr.jpg" alt="微信 ssjnpm 二维码" /></div>
      </section>
    </div>
  );
}

function App() {
  const [manifest, setManifest] = useState(FALLBACK_MANIFEST);
  const [recommendedKey, setRecommendedKey] = useState("windows:x86_64");
  const [selectedKey, setSelectedKey] = useState("windows:x86_64");
  const [contactOpen, setContactOpen] = useState(false);
  const [manifestLive, setManifestLive] = useState(false);

  useEffect(() => { startWebsiteAnalytics(); }, []);

  useEffect(() => {
    let active = true;
    fetch("/downloads/manifest.json", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("manifest unavailable")))
      .then((data) => {
        if (active && isDownloadManifest(data)) {
          setManifest(data);
          setManifestLive(true);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    detectRecommendedDownload(manifest.downloads, navigator).then((item) => {
      if (!active || !item) return;
      const key = `${item.platform}:${item.arch}`;
      setRecommendedKey(key);
      setSelectedKey(key);
    });
    return () => { active = false; };
  }, [manifest]);

  const selected = useMemo(() => manifest.downloads.find((item) => `${item.platform}:${item.arch}` === selectedKey) || manifest.downloads[0], [manifest, selectedKey]);

  return (
    <>
      <main id="top">
        <section className="relative min-h-[100svh] overflow-hidden bg-black text-white" aria-labelledby="hero-title">
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/images/hero-poster.jpg"
            aria-hidden="true"
            tabIndex={-1}
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/35" aria-hidden="true" />

          <div className="relative z-10 min-h-[100svh]">
            <header className="absolute inset-x-0 top-0 px-6 pt-6 md:px-12 lg:px-16" aria-label="SFast 导航">
              <div className="liquid-glass mx-auto flex max-w-7xl items-center justify-between rounded-xl px-4 py-2">
                <Brand inverse />
                <nav className="hidden items-center gap-8 text-sm text-white/80 md:flex" aria-label="主导航">
                  <a className="transition-colors hover:text-white" href="#workflow" onClick={() => trackWebsiteEvent("cta_click", { target: "nav_workflow", metadata: { cta: "nav_workflow" } })}>工作方式</a>
                  <a className="transition-colors hover:text-white" href="#capabilities" onClick={() => trackWebsiteEvent("cta_click", { target: "nav_capabilities", metadata: { cta: "nav_capabilities" } })}>产品能力</a>
                  <a className="transition-colors hover:text-white" href="#download" onClick={() => trackWebsiteEvent("cta_click", { target: "nav_download", metadata: { cta: "nav_download" } })}>软件下载</a>
                </nav>
                <button
                  className="inline-flex min-h-10 items-center justify-center rounded-lg bg-white px-4 text-sm font-medium text-black transition-colors hover:bg-gray-100 sm:px-6"
                  type="button"
                  onClick={() => {
                    trackWebsiteEvent("cta_click", { target: "authorization_header", metadata: { cta: "authorization_header" } });
                    setContactOpen(true);
                  }}
                >
                  <QrCode className="mr-2 h-4 w-4" aria-hidden="true" />
                  获取授权
                </button>
              </div>
            </header>

            <div className="flex min-h-[100svh] flex-col items-center justify-center px-6 pb-36 pt-36 text-center sm:pb-40 md:px-12 lg:px-16">
              <div className="flex w-full max-w-4xl flex-col items-center">
                <AnimatedHeading
                  id="hero-title"
                  text={'让每个直播账号\n都在同一节奏。'}
                  className="mb-5 text-4xl font-normal leading-[1.08] md:text-5xl lg:text-6xl xl:text-7xl"
                  style={{ letterSpacing: 0 }}
                  delay={200}
                  charDelay={30}
                />
                <FadeIn delay={800} duration={1000}>
                  <p className="hero-subheading text-base leading-7 text-white/75 md:text-lg md:leading-8">面向淘宝直播、小红书和抖音，把账号启动、直播间切换和评论发送收进一个工作台。</p>
                </FadeIn>
                <FadeIn delay={1200} duration={1000} className="mt-8">
                  <div className="flex flex-wrap justify-center gap-4">
                    <a
                      className="inline-flex items-center justify-center rounded-lg bg-white px-8 py-3 font-medium text-black transition-colors hover:bg-gray-100"
                      href="#download"
                      onClick={() => trackWebsiteEvent("cta_click", { target: "hero_download", metadata: { cta: "hero_download" } })}
                    >
                      <Download className="mr-2 h-5 w-5" aria-hidden="true" />
                      下载 SFast
                    </a>
                    <a
                      className="liquid-glass inline-flex items-center justify-center rounded-lg border border-white/20 px-8 py-3 font-medium text-white transition-colors hover:bg-white hover:text-black"
                      href="#workflow"
                      onClick={() => trackWebsiteEvent("cta_click", { target: "hero_workflow", metadata: { cta: "hero_workflow" } })}
                    >
                      了解产品
                    </a>
                  </div>
                </FadeIn>
              </div>
            </div>

            <div className="absolute inset-x-0 bottom-0 flex justify-center px-6 pb-8 sm:pb-10 lg:px-16 lg:pb-16">
              <FadeIn delay={1400} duration={1000}>
                <div className="liquid-glass rounded-xl border border-white/20 px-6 py-3 text-sm text-white/90 sm:text-base">淘宝直播 · 小红书 · 抖音</div>
              </FadeIn>
            </div>
          </div>
        </section>

        <section className="workflow" id="workflow" aria-labelledby="workflow-title">
          <div className="section-inner">
            <div className="section-intro">
              <span className="section-kicker"><Settings2 size={15} /> 一次完整操作</span>
              <h2 id="workflow-title">从账号就绪到结果确认，<br />始终在同一条工作流里。</h2>
            </div>
            <div className="workflow-track">
              {workflows.map((item, index) => {
                const Icon = item.icon;
                return (
                  <article className="workflow-step" key={item.title}>
                    <div className="step-marker"><span>{String(index + 1).padStart(2, "0")}</span><Icon /></div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="capabilities" id="capabilities" aria-labelledby="capability-title">
          <div className="section-inner">
            <div className="capability-heading">
              <span className="section-kicker"><Gauge size={15} /> 运营工作台</span>
              <h2 id="capability-title">不是更多窗口，<br />而是更清楚的状态。</h2>
              <p>围绕实际直播操作组织界面，不用在多个浏览器之间反复确认。</p>
            </div>
            <div className="capability-rows">
              <article>
                <div className="capability-icon"><Users /></div>
                <div><h3>账号状态一眼看清</h3><p>浏览器、网络/IP、内存、登录、直播间和页面状态集中呈现，支持批量启动、停止与排列窗口。</p></div>
                <span>ACCOUNT DESK</span>
              </article>
              <article>
                <div className="capability-icon"><Radio /></div>
                <div><h3>锁定直播间再执行</h3><p>选择正在直播的关注主播后，SFast 会记录目标直播间；发送前再次核验每个账号的实际直播间。</p></div>
                <span>ROOM LOCK</span>
              </article>
              <article>
                <div className="capability-icon"><Wifi /></div>
                <div><h3>每个账号独立配置</h3><p>账号使用独立浏览器环境，可按账号配置 HTTP、HTTPS 或 SOCKS5 代理，并单独测试连接。</p></div>
                <span>NETWORK</span>
              </article>
              <article>
                <div className="capability-icon"><Clipboard /></div>
                <div><h3>结果按账号核对</h3><p>发送结束后查看各账号触发数、成功数、状态和备注，快速定位未完成的操作。</p></div>
                <span>RESULT LOG</span>
              </article>
            </div>
          </div>
        </section>

        <section className="download-section" id="download" aria-labelledby="download-title">
          <div className="download-grid">
            <div className="download-copy">
              <span className="section-kicker light"><Download size={15} /> 官方下载</span>
              <h2 id="download-title">下载 SFast<br />开始整理直播操作。</h2>
              <p>当前版本 <strong>v{manifest.version}</strong>。安装后登录账号并输入授权码，即可开始添加运营账号。</p>
              <div className="release-status"><i /><span>{manifestLive ? "已读取线上发布清单" : "使用已验证的发布清单"}</span></div>
            </div>

            <div className="download-panel">
              <div className="platform-tabs" role="tablist" aria-label="选择下载平台">
                {manifest.downloads.map((item) => {
                  const key = `${item.platform}:${item.arch}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={selectedKey === key}
                      className={selectedKey === key ? "active" : ""}
                      onClick={() => setSelectedKey(key)}
                    >
                      <PlatformIcon platform={item.platform} />
                      <span>{item.label}<small>{key === recommendedKey ? "推荐" : item.arch === "aarch64" ? "ARM 设备" : ""}</small></span>
                    </button>
                  );
                })}
              </div>
              <div className="selected-download">
                <div className="download-os-icon"><PlatformIcon platform={selected.platform} /></div>
                <div className="selected-info">
                  <small>SF / {selected.arch.toUpperCase()}</small>
                  <h3>{selected.label}</h3>
                  <p>{selected.minimumOs} · {formatBytes(selected.size)}</p>
                </div>
                <a className="download-button" href={selected.url} download onClick={() => trackWebsiteEvent("download", { target: selected.url, metadata: { platform: selected.platform, architecture: selected.arch, version: manifest.version, label: selected.label } })}><Download />立即下载</a>
              </div>
              <details className="checksum-details">
                <summary><ShieldCheck size={15} />文件校验信息<ChevronDown size={15} /></summary>
                <code>{selected.sha256}</code>
              </details>
            </div>
          </div>
        </section>

        <section className="contact-band" aria-labelledby="contact-band-title">
          <div className="contact-band-inner">
            <div className="contact-symbol"><QrCode /></div>
            <div><span>LICENSE / SUPPORT</span><h2 id="contact-band-title">需要授权码？添加微信联系。</h2><p>微信号 <strong>ssjnpm</strong>，备注“SFast”。</p></div>
            <button type="button" onClick={() => { trackWebsiteEvent("cta_click", { target: "authorization_contact_band", metadata: { cta: "authorization_contact_band" } }); setContactOpen(true); }}>查看二维码<ExternalLink /></button>
          </div>
        </section>

        <section className="faq" aria-labelledby="faq-title">
          <div className="section-inner faq-grid">
            <div className="faq-heading"><span className="section-kicker"><CircleHelp size={15} /> 使用前了解</span><h2 id="faq-title">常见问题</h2></div>
            <div className="faq-list">
              {faqs.map(([question, answer]) => (
                <details key={question}><summary>{question}<ChevronDown /></summary><p>{answer}</p></details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div><Brand compact /><p>淘宝直播 · 小红书 · 抖音多账号运营控制台</p></div>
        <span>© 2026 SFast</span>
      </footer>

      {contactOpen && <ContactDialog onClose={() => setContactOpen(false)} onCopy={() => trackWebsiteEvent("cta_click", { target: "authorization_wechat_copy", metadata: { cta: "authorization_wechat_copy" } })} />}
    </>
  );
}

export default App;
